import { ReactNode } from 'react';
import createSafeContext from '@paradigm/utils/src/createSafeContext';
import { availableToken } from '@paradigm/api/src/internal/token';
import createPapiWebSocket from '@paradigm/api/src/internal/createPapiWebSocket';

import { useWebsocket } from '#/positions/contexts/abstractions/useWebsocket';
import {
  processFsPlatformState,
  processUMPlatformState,
} from '@paradigm/api/src/process/platform-state';
import {
  DataProcessor,
  DataProcessorsByChannel,
  PlatformStateNotification,
  PlatformStateWebSocket,
  RawPlatformStatesNotificationResp,
  WebSocketStatus,
} from './types';

export const [usePlatformStateWebsocket, PlatformStateWebsocketContext] =
  createSafeContext<PlatformStateWebSocket>('PlatformStateWebsocketContext');
export const [usePlatformStateWSStatus, PlatformStateWebsocketStatusContext] =
  createSafeContext<WebSocketStatus>('PlatformStateWebsocketStatusContext');

interface Props {
  children: ReactNode;
  wsAddress: string;
  useNanoSeconds: boolean;
}

export function PlatformStateWebsocketProvider({
  children,
  wsAddress,
  useNanoSeconds,
}: Props) {
  const { ws, status } = useWebsocket<
    PlatformStateWebSocket,
    PlatformStateNotification
  >(() => createPlatformStateWebSocketWithConfig(wsAddress, useNanoSeconds));
  return (
    <PlatformStateWebsocketContext value={ws}>
      <PlatformStateWebsocketStatusContext value={status}>
        {children}
      </PlatformStateWebsocketStatusContext>
    </PlatformStateWebsocketContext>
  );
}

function createPlatformStateWebSocketWithConfig(
  wsAddress: string,
  useNanoSeconds: boolean,
): PlatformStateWebSocket {
  return createPapiWebSocket({
    urlProvider: async () => urlProvider(wsAddress),
    processNotification: createProcessNotification(useNanoSeconds),
  });
}

async function urlProvider(wsAddress?: string) {
  const token = await availableToken();
  return (
    `${wsAddress}` +
    '?cancel-on-disconnect=0' +
    `&token=${encodeURIComponent(token)}`
  );
}

function createProcessNotification(
  useNanoSeconds: boolean,
): (raw: RawPlatformStatesNotificationResp) => PlatformStateNotification {
  const dataProcessors: DataProcessorsByChannel = {
    platform_state(notif) {
      return useNanoSeconds
        ? processFsPlatformState(notif)
        : processUMPlatformState(notif);
    },
  };

  return (raw: RawPlatformStatesNotificationResp) => {
    const { channel, data } = raw.params;
    const processor = dataProcessors[channel] as DataProcessor;
    return {
      params: {
        channel,
        data: processor(data),
      },
    } as PlatformStateNotification;
  };
}
